import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { type ScrollView, StyleSheet, View } from 'react-native';

import { useAuthContext, useCRMEventsContext } from '@fhs/auth/src/hooks';
import { Button, tokens } from '@fhs/ui';
import RateLimitedErrorModal from '@fhs-legacy/frontend/src/pages/cart/cart-rate-limited-modal';
import { logCartContinueOrder } from '@fhs-legacy/frontend/src/pages/cart/payment/utils';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { CART_PAGE_CONTINUE_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import CartErrorAlerts from '../cart-error-alerts';
import type { CartErrorButtonState } from '../cart-error-alerts/get-cart-error-state';

import { useContinueButtonStates } from './use-continue-button-states';

/** Release the retry spinner if the reprice never gets off the ground. */
const PRICING_RETRY_TIMEOUT_MS = 2000;

interface IContinueButtonProps {
  scrollViewRef: React.MutableRefObject<ScrollView>;
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ContinueButton({
  scrollViewRef,
  setIsBadContinueAttempt,
}: IContinueButtonProps) {
  const [errorState, setErrorState] = useState<CartErrorButtonState>({
    hasError: false,
    canRetryPricing: false,
  });
  const [isRetryingPricing, setIsRetryingPricing] = useState(false);
  const pricingStartedRef = useRef(false);
  const { isAuthenticated } = useAuthContext();
  const { logRBIEvent } = useCRMEventsContext();
  const { tryPricing, serverOrderLoading } = useCartContext();

  const { isDisabledForBadState, isLoading, shouldContinue } = useContinueButtonStates();
  const showSignUpToContinue = useFlag('enable-signup-cart-button-text')

  // Hold the spinner until the reprice we asked for actually settles. Mirrors the
  // commit-retry guard on the checkout button: tryPricing() can return without
  // firing (no user, store unavailable, cart empty), so never wait forever.
  useEffect(() => {
    if (!isRetryingPricing) {
      pricingStartedRef.current = false;
      return undefined;
    }

    if (serverOrderLoading) {
      pricingStartedRef.current = true;
      return undefined;
    }

    if (pricingStartedRef.current) {
      setIsRetryingPricing(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      if (!pricingStartedRef.current) {
        setIsRetryingPricing(false);
      }
    }, PRICING_RETRY_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isRetryingPricing, serverOrderLoading]);

  const showPricingRetry = errorState.canRetryPricing && !isLoading;
  const isBusy = isLoading || isRetryingPricing;
  // A retry in flight keeps the button busy, so it must not also look pressable.
  const isDisabled =
    isRetryingPricing || (!showPricingRetry && (isDisabledForBadState || errorState.hasError));

  const shouldNavigateToSignin = shouldContinue && !isAuthenticated && showSignUpToContinue;
  const shouldNavigateToCheckout =
    (shouldContinue && isAuthenticated) ||
    (shouldContinue && !showSignUpToContinue && !isAuthenticated);

  const handleRetryPricing = () => {
    if (isRetryingPricing) {
      return;
    }
    setIsRetryingPricing(true);
    tryPricing();
  };

  const handleContinueInCart = () => {
    /*  The continue button should show signin if the flag is enabled and the user is not logged in.
     *  This will take them to signin and return to the cart after completed. When the flag is turned off,
     *  the user will see the continue button and navigate to checkout. The user must have a donation
     *  amount selected to continue in any case.
     */

    logCartContinueOrder(logRBIEvent);
    // Open upsell modal, do we want to make this conditional with a flag?
    // router.setParams({ action: 'upsell', id: 'continue' });
    if (shouldNavigateToSignin) {
      router.navigate(routes.signIn);
    } else if (shouldNavigateToCheckout) {
      router.navigate(routes.cartPayment);
    } else {
      // Donation is required but not selected - scroll to donation section
      setIsBadContinueAttempt(true);
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }
  };

  return (
    <View style={styles.container}>
      <CartErrorAlerts cartButtonLoading={isBusy} setErrorState={setErrorState} />
      <RateLimitedErrorModal />

      <Button
        testID={CART_PAGE_CONTINUE_BUTTON}
        size="xl"
        loading={isBusy}
        disabled={isDisabled}
        onPress={showPricingRetry ? handleRetryPricing : handleContinueInCart}
        accessibilityRole="button"
        accessibilityLabel={
          showPricingRetry
            ? 'Try pricing your order again'
            : shouldNavigateToSignin
              ? 'Sign up to continue with your order'
              : 'Continue to checkout'
        }
        accessibilityHint={
          showPricingRetry
            ? 'Retries pricing your order'
            : isDisabled
              ? 'Please check the items in your cart and try again.'
              : 'Continues to the checkout screen'
        }
        accessibilityState={{ disabled: isDisabled, busy: isBusy }}
      >
        <Button.Text style={isDisabled ? styles.disabledText : undefined}>
          {showPricingRetry
            ? 'Try Again'
            : shouldNavigateToSignin
              ? 'Sign Up to Continue'
              : 'Continue'}
        </Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: tokens.colors.$blackOpacity10,
    width: '100%',
    padding: 16,
  },
  disabledText: { color: 'black' },
});
